import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Image,
  SimpleGrid,
  Group,
  Box,
  CopyButton,
} from "@mantine/core";
import {
  IconApps,
  IconCheck,
  IconCopy,
  IconShield,
} from "@tabler/icons-react";

import { MODAL_WIDTH_FOR_DATA_INTENSIVE_MODALS } from "../../../helpers/constants";
import { BOB_MODALS } from "../../helpers/constants";
import { Bob } from "../../stores/Bob";
import TwoFactorInput from "../details/twoFactorInput";
import { CustomModalTitle } from "../details/customModalTitle";

const NWC_LOGO_URL =
  "https://daxwktpz4hkwd.cloudfront.net/nwc_logos/nwc-square-color-two.png";

const chooserPaneStyle: React.CSSProperties = {
  minHeight: 190,
  border: "1px solid rgba(255, 255, 255, 0.14)",
  borderRadius: 8,
  padding: 16,
  background: "rgba(15, 23, 42, 0.32)",
};

const NwcEntry = observer(({ bob }: { bob: Bob }) => {
  const handleClose = () => {
    bob.setOpenModal(null);
    bob.purgeNostrConnections();
    bob.checkIfWeNeedToShowTwoFactorForm();
  };

  const [twoFACode, setTwoFACode] = useState<string>("");
  const [isOpeningPayCapableCodes, setIsOpeningPayCapableCodes] =
    useState(false);

  useEffect(() => {
    bob.checkIfWeNeedToShowTwoFactorForm();
    if (!bob.receiveOnlyNostrConnection && !bob.nostrConnectionsAreRefreshing) {
      void bob.updateNostrConnections("refresh-receive-only", null);
    }
  }, [bob]);

  const handleTryToGetNostrConnections = async () => {
    setIsOpeningPayCapableCodes(true);
    const didLoad = await bob.updateNostrConnections(
      "refresh-all",
      null,
      twoFACode,
    );
    setIsOpeningPayCapableCodes(false);
    if (didLoad) {
      bob.setOpenModal(BOB_MODALS.NWC_SECURE);
    }
  };

  const readOnlyConnection = bob.receiveOnlyNostrConnection;
  const isLoadingReceiveOnlyCode =
    !readOnlyConnection && bob.nostrConnectionsAreRefreshing;
  const payCapableNeedsTwoFactorCode =
    bob.workspace.accountRequiresTwoFactorFormForActions &&
    !/^\d{6}$/.test(twoFACode);
  const payCapableButtonIsDisabled =
    bob.areWeWaitingOnRails ||
    payCapableNeedsTwoFactorCode ||
    (bob.nostrConnectionsAreRefreshing && !isOpeningPayCapableCodes);

  return (
    <Modal
      opened={bob.openModal === BOB_MODALS.NWC_ENTRY}
      onClose={handleClose}
      size={MODAL_WIDTH_FOR_DATA_INTENSIVE_MODALS}
      title={
        <CustomModalTitle
          text="Get NWC Codes"
          icon={
            <Image
              src={NWC_LOGO_URL}
              alt="NWC Logo"
              w={16}
              h={16}
              fit="contain"
              style={{ flexShrink: 0, marginTop: 2 }}
            />
          }
        />
      }
    >
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <Box style={chooserPaneStyle}>
          <Stack h="100%" justify="space-between" gap="md">
            <Stack gap={6}>
              <Group gap="xs" wrap="nowrap">
                <IconShield size={20} />
                <Text fw={700}>Receive-only code</Text>
              </Group>
              <Text size="sm" c="dimmed">
                Copy this for apps that only need to receive. It cannot spend
                funds from your vault.
              </Text>
            </Stack>

            {readOnlyConnection ? (
              <CopyButton value={readOnlyConnection.nwc_uri}>
                {({ copied, copy }) => (
                  <Button
                    fullWidth
                    color={copied ? "teal" : "blue"}
                    leftSection={
                      copied ? <IconCheck size={16} /> : <IconCopy size={16} />
                    }
                    onClick={copy}
                  >
                    {copied ? "Copied!" : "Copy receive-only code"}
                  </Button>
                )}
              </CopyButton>
            ) : (
              <Button
                fullWidth
                variant="default"
                loading={isLoadingReceiveOnlyCode}
                leftSection={<IconCopy size={16} />}
                onClick={() =>
                  void bob.updateNostrConnections("refresh-receive-only", null)
                }
              >
                Get receive-only code
              </Button>
            )}
          </Stack>
        </Box>

        <Box style={chooserPaneStyle}>
          <Stack h="100%" justify="space-between" gap="md">
            <Stack gap={6}>
              <Group gap="xs" wrap="nowrap">
                <IconApps size={20} />
                <Text fw={700}>Code you can pay with</Text>
              </Group>
              <Text size="sm" c="dimmed">
                For trusted apps only. These codes can spend funds from your
                vault.
              </Text>
              <TwoFactorInput
                visible={bob.workspace.accountRequiresTwoFactorFormForActions}
                code={twoFACode}
                onCodeChange={setTwoFACode}
              />
            </Stack>

            <Button
              fullWidth
              leftSection={
                <Image
                  src={NWC_LOGO_URL}
                  alt="NWC Logo"
                  w={16}
                  h={16}
                  fit="contain"
                  style={{ flexShrink: 0, marginTop: 2 }}
                />
              }
              loading={isOpeningPayCapableCodes}
              disabled={payCapableButtonIsDisabled}
              onClick={() => handleTryToGetNostrConnections()}
            >
              Get a code you can pay with
            </Button>
          </Stack>
        </Box>
      </SimpleGrid>
    </Modal>
  );
});

export default NwcEntry;
